import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as svc from '../../services/staffService';
import type { StaffMember, AttendanceRecord, StaffTarget, ScheduleEntry } from '../../types';

interface StaffState {
  list:       StaffMember[];
  total:      number;
  selected:   StaffMember | null;
  attendance: AttendanceRecord[];
  schedule:   { weekStart: string; entries: ScheduleEntry[]; staff: StaffMember[] };
  targets:    StaffTarget[];
  loading:    boolean;
  error:      string | null;
}

const initialState: StaffState = {
  list: [], total: 0, selected: null, attendance: [],
  schedule: { weekStart: '', entries: [], staff: [] },
  targets: [], loading: false, error: null,
};

type ApiError = { response?: { data?: { message?: string } } };
const msg = (e: unknown) => (e as ApiError).response?.data?.message ?? 'Request failed';

// ─── Staff CRUD thunks ─────────────────────────────────────────────────────────
export const fetchStaff = createAsyncThunk(
  'staff/fetchAll',
  async (params: { role?: string; status?: string; q?: string; page?: number; limit?: number } | undefined, { rejectWithValue }) => {
    try { return (await svc.fetchStaff(params)).data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

export const fetchStaffById = createAsyncThunk('staff/fetchById', async (id: string, { rejectWithValue }) => {
  try { return (await svc.fetchStaffById(id)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});

export const createStaff = createAsyncThunk(
  'staff/create',
  async (data: Partial<StaffMember> & { password?: string }, { rejectWithValue }) => {
    try { return (await svc.createStaff(data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

export const updateStaff = createAsyncThunk(
  'staff/update',
  async ({ id, data }: { id: string; data: Partial<StaffMember> & { password?: string } }, { rejectWithValue }) => {
    try { return (await svc.updateStaff(id, data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

export const changeStaffStatus = createAsyncThunk(
  'staff/changeStatus',
  async ({ id, action }: { id: string; action: 'activate' | 'deactivate' | 'terminate' }, { rejectWithValue }) => {
    try { return (await svc.changeStaffStatus(id, action)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

export const uploadPhoto = createAsyncThunk(
  'staff/uploadPhoto',
  async ({ id, file }: { id: string; file: File }, { rejectWithValue }) => {
    try { return (await svc.uploadStaffPhoto(id, file)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

// ─── Attendance thunks ──────────────────────────────────────────────────────────
export const fetchAttendance = createAsyncThunk(
  'staff/fetchAttendance',
  async ({ month, staffId }: { month: string; staffId?: string }, { rejectWithValue }) => {
    try { return (await svc.fetchAttendance(month, staffId)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

export const markAttendance = createAsyncThunk(
  'staff/markAttendance',
  async (payload: { staffId: string; date: string; type: string; checkInTime?: string; checkOutTime?: string; leaveType?: string; notes?: string }, { rejectWithValue }) => {
    try { return (await svc.markAttendance(payload)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

// ─── Schedule thunks ─────────────────────────────────────────────────────────────
export const fetchSchedule = createAsyncThunk('staff/fetchSchedule', async (week: string, { rejectWithValue }) => {
  try { return (await svc.fetchSchedule(week)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});

export const saveSchedule = createAsyncThunk(
  'staff/saveSchedule',
  async ({ week, entries }: { week: string; entries: ScheduleEntry[] }, { rejectWithValue }) => {
    try { return (await svc.saveSchedule(week, entries)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

// ─── Target thunks ───────────────────────────────────────────────────────────────
export const fetchStaffTarget = createAsyncThunk(
  'staff/fetchTarget',
  async ({ staffId, month }: { staffId: string; month: string }, { rejectWithValue }) => {
    try { return (await svc.fetchStaffTarget(staffId, month)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

export const saveStaffTarget = createAsyncThunk(
  'staff/saveTarget',
  async (
    { staffId, month, data }: { staffId: string; month: string; data: { revenue: number; services: number; newClients: number; productSales: number } },
    { rejectWithValue },
  ) => {
    try { return (await svc.saveStaffTarget(staffId, month, data)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
  },
);

export const fetchAllTargets = createAsyncThunk('staff/fetchAllTargets', async (month: string, { rejectWithValue }) => {
  try { return (await svc.fetchAllTargets(month)).data.data; } catch (e) { return rejectWithValue(msg(e)); }
});

// ─── Slice ────────────────────────────────────────────────────────────────────
const staffSlice = createSlice({
  name: 'staff',
  initialState,
  reducers: {
    clearSelected: (s) => { s.selected = null; },
    clearError:    (s) => { s.error    = null; },
  },
  extraReducers: (builder) => {
    const pending  = (s: StaffState) => { s.loading = true;  s.error = null; };
    const rejected = (s: StaffState, a: { payload: unknown }) => { s.loading = false; s.error = a.payload as string; };
    const upsert   = (s: StaffState, member: StaffMember) => {
      const i = s.list.findIndex(m => m._id === member._id);
      if (i !== -1) s.list[i] = member; else s.list.unshift(member);
      if (s.selected?._id === member._id) s.selected = member;
    };

    builder
      .addCase(fetchStaff.pending,   pending)
      .addCase(fetchStaff.fulfilled, (s, a) => { s.loading = false; s.list = a.payload.data; s.total = a.payload.total; })
      .addCase(fetchStaff.rejected,  rejected)

      .addCase(fetchStaffById.fulfilled, (s, a) => { s.selected = a.payload; upsert(s, a.payload); })
      .addCase(createStaff.fulfilled,    (s, a) => { upsert(s, a.payload); })
      .addCase(updateStaff.fulfilled,    (s, a) => { upsert(s, a.payload); })
      .addCase(changeStaffStatus.fulfilled, (s, a) => { upsert(s, a.payload); })
      .addCase(uploadPhoto.fulfilled,    (s, a) => { upsert(s, a.payload); })

      .addCase(fetchAttendance.pending,   pending)
      .addCase(fetchAttendance.fulfilled, (s, a) => { s.loading = false; s.attendance = a.payload; })
      .addCase(fetchAttendance.rejected,  rejected)
      .addCase(markAttendance.fulfilled,  (s, a) => {
        const i = s.attendance.findIndex(r => r._id === a.payload._id);
        if (i !== -1) s.attendance[i] = a.payload; else s.attendance.push(a.payload);
      })
      .addCase(fetchSchedule.pending,   pending)
      .addCase(fetchSchedule.fulfilled, (s, a) => { s.loading = false; s.schedule = a.payload; })
      .addCase(fetchSchedule.rejected,  rejected)
      .addCase(saveSchedule.fulfilled,  (s, a) => { s.schedule.entries = a.payload; })

      .addCase(fetchAllTargets.pending,   pending)
      .addCase(fetchAllTargets.fulfilled, (s, a) => { s.loading = false; s.targets = a.payload; })
      .addCase(fetchAllTargets.rejected,  rejected)
      .addCase(saveStaffTarget.fulfilled, (s, a) => {
        const i = s.targets.findIndex(t => (typeof t.staff === 'string' ? t.staff : t.staff._id) === (typeof a.payload.staff === 'string' ? a.payload.staff : a.payload.staff._id));
        if (i !== -1) s.targets[i] = a.payload;
      });
  },
});

export const { clearSelected, clearError } = staffSlice.actions;
export default staffSlice.reducer;
